import { useState, useEffect } from 'react';
import { Link, useParams } from 'react-router-dom';
import { Loader2, Facebook, Link as LinkIcon, Check } from 'lucide-react';
import { Button } from '../components/ui/button';
import { ArticleCard, TEAM_META } from '../components/custom/ArticleCard';
import { useSeo } from '../components/custom/Seo';
import axios from 'axios';
import { logError } from '../lib/logger';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const XIcon = ({ className }) => (
  <svg viewBox="0 0 24 24" fill="currentColor" className={className} aria-hidden="true">
    <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z" />
  </svg>
);

const isHeadingLine = (line) =>
  line.length > 2 && line.length < 90 && line === line.toUpperCase() && /[A-Z]/.test(line) && !/^["'-]/.test(line);

const ArticleBody = ({ content }) => {
  const lines = content.split('\n').map((l) => l.trim());
  const blocks = [];
  let list = null;

  lines.forEach((line) => {
    if (line.startsWith('- ')) {
      if (!list) {
        list = [];
        blocks.push({ type: 'list', items: list });
      }
      list.push(line.slice(2));
      return;
    }
    list = null;
    if (!line) return;
    if (line.startsWith('> ')) blocks.push({ type: 'quote', text: line.slice(2) });
    else if (isHeadingLine(line)) blocks.push({ type: 'heading', text: line });
    else blocks.push({ type: 'p', text: line });
  });

  return (
    <div data-testid="article-body">
      {blocks.map((block, i) => {
        const key = `${block.type}-${i}-${(block.text || (block.items && block.items[0]) || '').slice(0, 24)}`;
        if (block.type === 'heading') {
          return (
            <h2 key={key} className="font-heading text-lg md:text-lg text-slate-900 uppercase tracking-wide mt-10 mb-4">
              {block.text}
            </h2>
          );
        }
        if (block.type === 'list') {
          return (
            <ul key={key} className="list-disc pl-6 my-5 space-y-2 text-slate-800 text-lg leading-[1.8]">
              {block.items.map((item, j) => (
                <li key={`${j}-${item.slice(0, 24)}`}>{item}</li>
              ))}
            </ul>
          );
        }
        if (block.type === 'quote') {
          return (
            <blockquote
              key={key}
              className="border-l-4 border-[#BF0A30] pl-5 my-7 text-slate-700 text-xl leading-relaxed font-serif-head italic"
            >
              {block.text}
            </blockquote>
          );
        }
        return (
          <p key={key} className="text-slate-800 text-lg leading-[1.8] mb-6">
            {block.text}
          </p>
        );
      })}
    </div>
  );
};

const ShareControls = ({ article, size = 'sm' }) => {
  const [copied, setCopied] = useState(false);
  const shareUrl = `${BACKEND_URL}/api/share/article/${article.id}`;
  const btn = `inline-flex items-center justify-center border border-slate-300 text-slate-600 hover:text-white transition-colors ${
    size === 'sm' ? 'w-9 h-9' : 'w-10 h-10'
  }`;

  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(shareUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      logError('Failed to copy:', err);
    }
  };

  return (
    <div className="flex items-center gap-2">
      <button
        onClick={() =>
          window.open(`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(shareUrl)}`, '_blank')
        }
        data-testid="share-facebook"
        className={`${btn} hover:bg-[#1877F2] hover:border-[#1877F2]`}
        aria-label="Share on Facebook"
      >
        <Facebook className="w-4 h-4" />
      </button>
      <button
        onClick={() =>
          window.open(
            `https://twitter.com/intent/tweet?url=${encodeURIComponent(shareUrl)}&text=${encodeURIComponent(
              `${article.title} — DC Sports Report`
            )}`,
            '_blank'
          )
        }
        data-testid="share-twitter"
        className={`${btn} hover:bg-black hover:border-black`}
        aria-label="Share on X"
      >
        <XIcon className="w-4 h-4" />
      </button>
      <button
        onClick={copyLink}
        data-testid="copy-link"
        className={`${btn} ${copied ? 'bg-green-600 border-green-600 text-white' : 'hover:bg-[#002868] hover:border-[#002868]'}`}
        aria-label="Copy link"
      >
        {copied ? <Check className="w-4 h-4" /> : <LinkIcon className="w-4 h-4" />}
      </button>
    </div>
  );
};

const formatDate = (dateString) => {
  if (!dateString) return '';
  try {
    return new Date(dateString).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' });
  } catch {
    return dateString;
  }
};

const ArticlePage = () => {
  const { articleId } = useParams();
  const [article, setArticle] = useState(null);
  const [related, setRelated] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const seoMeta = article ? TEAM_META[article.team] || TEAM_META.nationals : null;
  useSeo({
    title: article ? `${article.title} | DC Sports Report` : undefined,
    description: article
      ? article.summary || (article.content || '').substring(0, 160).trim()
      : undefined,
    path: `/article/${articleId}`,
    type: 'article',
    image: article && article.has_image
      ? `https://dcsportsreport.com/api/articles/${article.id}/image`
      : undefined,
    jsonLd: article
      ? [
          {
            '@context': 'https://schema.org',
            '@type': 'NewsArticle',
            headline: article.title,
            description: article.summary || undefined,
            image: article.has_image
              ? [`https://dcsportsreport.com/api/articles/${article.id}/image`]
              : undefined,
            datePublished: article.created_at || undefined,
            author: article.author ? [{ '@type': 'Person', name: article.author }] : undefined,
            publisher: {
              '@type': 'NewsMediaOrganization',
              name: 'DC Sports Report',
              url: 'https://dcsportsreport.com/',
              logo: {
                '@type': 'ImageObject',
                url: 'https://customer-assets.emergentagent.com/job_dcsportshub/artifacts/fw8w9adx_DCSportsReport_Logo.png',
              },
            },
            mainEntityOfPage: `https://dcsportsreport.com/article/${article.id}`,
          },
          {
            '@context': 'https://schema.org',
            '@type': 'BreadcrumbList',
            itemListElement: [
              { '@type': 'ListItem', position: 1, name: 'Home', item: 'https://dcsportsreport.com/' },
              {
                '@type': 'ListItem',
                position: 2,
                name: seoMeta.full,
                item: `https://dcsportsreport.com${seoMeta.path}`,
              },
              { '@type': 'ListItem', position: 3, name: article.title },
            ],
          },
        ]
      : undefined,
  });

  useEffect(() => {
    window.scrollTo(0, 0);
    const fetchAll = async () => {
      setLoading(true);
      try {
        const response = await axios.get(`${API}/articles/${articleId}`);
        setArticle(response.data);
        try {
          const all = await axios.get(`${API}/articles`);
          const others = (all.data.articles || []).filter(
            (a) => a.id !== articleId && a.published !== false
          );
          const sameTeam = others.filter((a) => a.team === response.data.team);
          const rest = others.filter((a) => a.team !== response.data.team);
          setRelated([...sameTeam, ...rest].slice(0, 3));
        } catch (relatedErr) {
          logError('Failed to load related articles:', relatedErr);
          setRelated([]);
        }
      } catch (err) {
        setError('Article not found');
      } finally {
        setLoading(false);
      }
    };
    fetchAll();
  }, [articleId]);

  if (loading) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <Loader2 className="w-8 h-8 text-[#002868] animate-spin" />
      </div>
    );
  }

  if (error || !article) {
    return (
      <div className="min-h-screen bg-white flex flex-col items-center justify-center">
        <p className="text-xl text-slate-600 mb-4">Article not found</p>
        <Link to="/">
          <Button className="rounded-none bg-[#002868] hover:bg-[#001d4d]">Back to Home</Button>
        </Link>
      </div>
    );
  }

  const meta = TEAM_META[article.team] || TEAM_META.nationals;
  const accent = meta.color === '#000000' ? '#BF0A30' : meta.color;
  const imageUrl = article.has_image ? `${API}/articles/${article.id}/image` : null;

  return (
    <div data-testid="article-page" className="min-h-screen bg-white">
      <article className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 pt-10 pb-8">
        {/* Header */}
        <header>
          <Link
            to={meta.path}
            data-testid="article-team-link"
            className="text-xs font-bold uppercase tracking-[0.25em] hover:underline underline-offset-4"
            style={{ color: accent }}
          >
            {meta.full}
          </Link>

          <h1
            data-testid="article-headline"
            className="font-serif-head font-extrabold text-3xl sm:text-4xl lg:text-5xl text-slate-900 leading-tight tracking-tight mt-3"
          >
            {article.title}
          </h1>

          {article.summary && (
            <p data-testid="article-deck" className="text-slate-600 text-lg sm:text-xl leading-relaxed mt-4 max-w-3xl">
              {article.summary}
            </p>
          )}

          {/* Byline row */}
          <div className="flex flex-wrap items-center justify-between gap-4 mt-6 pt-5 border-t border-slate-200">
            <div className="text-sm text-slate-600">
              {article.author && (
                <p>
                  By{' '}
                  <span data-testid="article-author" className="font-bold text-slate-900">
                    {article.author}
                  </span>
                </p>
              )}
              <p className="text-slate-500 mt-0.5">
                {article.created_at && <span data-testid="article-published-date">Published {formatDate(article.created_at)}</span>}
                {article.updated_at && (
                  <span data-testid="article-updated-date"> · Updated {formatDate(article.updated_at)}</span>
                )}
              </p>
            </div>
            <ShareControls article={article} />
          </div>
        </header>

        {/* Hero Image */}
        {imageUrl && (
          <figure className="mt-7">
            <img
              data-testid="article-featured-image"
              src={imageUrl}
              alt={article.title}
              className="w-full h-auto bg-slate-100 border border-slate-200"
              onError={(e) => {
                e.currentTarget.parentElement.style.display = 'none';
              }}
            />
          </figure>
        )}

        {/* Body */}
        <div className="max-w-2xl mx-auto mt-10">
          <ArticleBody content={article.content} />

          {/* End rule + share */}
          <div className="mt-12 pt-6 border-t border-slate-200 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <p className="font-heading text-sm text-slate-900 uppercase tracking-[0.2em]">
              Share this story
            </p>
            <ShareControls article={article} />
          </div>
        </div>
      </article>

      {/* Related Coverage */}
      {related.length > 0 && (
        <section data-testid="related-coverage" className="bg-slate-50 border-t border-slate-200">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
            <div className="flex items-end justify-between border-b border-slate-200 pb-3 mb-6">
              <h2 className="font-heading text-lg md:text-lg text-slate-900 uppercase tracking-wide flex items-center gap-3">
                <span className="inline-block w-1 h-6" style={{ backgroundColor: accent }} />
                More From DC Sports Report
              </h2>
              <Link
                to={meta.path}
                className="text-xs font-bold uppercase tracking-[0.15em] text-slate-500 hover:text-[#BF0A30] transition-colors"
              >
                More {meta.label} Coverage
              </Link>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-6">
              {related.map((a) => (
                <ArticleCard key={a.id} article={a} />
              ))}
            </div>
          </div>
        </section>
      )}
    </div>
  );
};

export default ArticlePage;
